import React from 'react';
import { SessionPub } from '../api/types';
import { formatScanTime } from '../utils/dateUtils';
import { openDirections } from '../utils/directions';
import { SendIcon } from './ui/icons';
import './PubRow.css';

interface PubRowProps {
  pub: SessionPub;
  /** Position on the crawl, zero-based; shown as 1, 2, 3… */
  index: number;
  /** A list item in the pubs tab, a plain block inside the map popup */
  as?: 'li' | 'div';
  className?: string;
  testId?: string;
  /** Scanned on this device but not yet confirmed by the server */
  pending?: boolean;
}

/**
 * One pub: number, name, scan time, the Done stamp and the directions button.
 * Shared by the pubs list and the map popup so both read the same.
 */
const PubRow: React.FC<PubRowProps> = ({ pub, index, as: Tag = 'li', className, testId = 'pub-row', pending = false }) => {
  const completed = pub.isComplete;
  const showPending = pending && !completed;
  return (
    <Tag
      className={['pub-row', completed && 'pub-row-done', showPending && 'pub-row-pending', className].filter(Boolean).join(' ')}
      data-testid={testId}
      data-completed={completed ? 'true' : 'false'}
      data-pending={showPending ? 'true' : 'false'}
    >
      <span className="pub-row-num">{index + 1}</span>
      <span className="pub-row-pub">
        <b>{pub.name}</b>
        {completed && pub.completionTime && (
          <span className="pub-row-time" data-testid="pub-scan-time">
            Scanned at {formatScanTime(pub.completionTime)}
          </span>
        )}
        {showPending && (
          <span className="pub-row-time">Scanned, waiting for signal to sync</span>
        )}
      </span>
      {showPending && (
        <span className="pub-row-stamp pub-row-stamp-pending" data-testid="pub-pending-stamp">
          Pending sync
        </span>
      )}
      {completed && (
        <span className="pub-row-stamp" data-testid="pub-done-stamp">
          Done
        </span>
      )}
      <button
        type="button"
        className="pub-row-go"
        onClick={() => openDirections(pub)}
        aria-label={`Directions to ${pub.name}`}
        data-testid="pub-directions"
      >
        <SendIcon />
      </button>
    </Tag>
  );
};

export default PubRow;
